import { CalendarRange, Gamepad2 } from 'lucide-react'

import {
  AVAILABILITY_HEATMAP,
  DAY_LABELS,
  HOUR_BANDS,
  PEAK_WINDOW_LABEL,
  TOP_GAME_LABEL,
} from '@/features/marketing/mockData/availability'

/**
 * Weekly availability heat-map mockup. Demonstrates the "schedule
 * against real availability, not guesswork" pitch for the Squadrons
 * page. Cells deepen blue as the count of available members rises,
 * so the peak window leaps out visually.
 */
export function AvailabilityMockup() {
  // Compute max for normalisation — using the actual peak rather than
  // a hard-coded "20" so the heat-map auto-balances if the mock data
  // ever changes.
  const max = Math.max(...AVAILABILITY_HEATMAP.flat())
  return (
    <div className="p-6">
      <div className="flex items-baseline justify-between">
        <p className="font-display text-[10px] font-bold tracking-[0.2em] text-fg-subtle uppercase">
          Squadron availability · this week
        </p>
        <p className="font-display text-[10px] tracking-[0.15em] text-fg-subtle uppercase">
          Out of 47
        </p>
      </div>

      <div className="mt-4">
        <div className="grid grid-cols-[auto_repeat(8,1fr)] gap-1 text-[10px]">
          {/* Column headers — hour bands */}
          <div />
          {HOUR_BANDS.map((h) => (
            <div
              key={h}
              className="font-display text-center font-bold tracking-wider text-fg-subtle"
            >
              {h}
            </div>
          ))}
          {/* Rows: day label + heat cells */}
          {DAY_LABELS.map((day, dayIdx) => (
            <FragmentRow
              key={day}
              dayLabel={day}
              row={AVAILABILITY_HEATMAP[dayIdx]}
              max={max}
            />
          ))}
        </div>
      </div>

      {/* Legend strip */}
      <div className="mt-4 flex items-center gap-2 text-[10px] text-fg-subtle">
        <span className="font-display tracking-wider uppercase">Low</span>
        <div className="flex flex-1 items-center gap-0.5">
          {[0.1, 0.3, 0.5, 0.7, 0.9].map((v) => (
            <span
              key={v}
              className="h-1.5 flex-1 rounded-sm"
              style={{ backgroundColor: heatColor(v, 1) }}
            />
          ))}
        </div>
        <span className="font-display tracking-wider uppercase">High</span>
      </div>

      {/* Read-out tiles below the heat-map. */}
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-primary/30 bg-primary/[0.06] p-3">
          <p className="font-display flex items-center gap-1.5 text-[10px] font-bold tracking-[0.18em] text-primary uppercase">
            <CalendarRange className="size-3" aria-hidden />
            Peak window
          </p>
          <p className="mt-1.5 text-sm font-semibold text-fg">
            {PEAK_WINDOW_LABEL}
          </p>
        </div>
        <div className="rounded-lg border border-secondary/30 bg-secondary/[0.06] p-3">
          <p className="font-display flex items-center gap-1.5 text-[10px] font-bold tracking-[0.18em] text-secondary uppercase">
            <Gamepad2 className="size-3" aria-hidden />
            Top game
          </p>
          <p className="mt-1.5 text-sm font-semibold text-fg">
            {TOP_GAME_LABEL}
          </p>
        </div>
      </div>
    </div>
  )
}

function FragmentRow({
  dayLabel,
  row,
  max,
}: {
  dayLabel: string
  row: number[]
  max: number
}) {
  return (
    <>
      <div className="font-display flex items-center justify-end pr-1 text-[10px] font-bold tracking-wider text-fg-subtle">
        {dayLabel}
      </div>
      {row.map((v, i) => (
        <div
          key={i}
          className="aspect-square rounded-sm"
          style={{ backgroundColor: heatColor(v, max) }}
          title={`${v} available`}
        />
      ))}
    </>
  )
}

/**
 * Map a 0..max count to a blue-spectrum colour. Lowest = transparent
 * border-coloured (reads as "no signal"), highest = primary blue at
 * full saturation. The non-linear ramp (sqrt) makes mid-values pop a
 * bit more than a pure-linear interpolation would.
 */
function heatColor(value: number, max: number): string {
  if (max <= 0 || value <= 0) return 'rgba(255,255,255,0.04)'
  const t = Math.sqrt(value / max)
  // Primary blue gradient, 12% → 92% alpha.
  const alpha = 0.12 + t * 0.8
  return `rgba(61, 111, 255, ${alpha.toFixed(2)})`
}
